import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  ClickAwayListener,
  IconButton,
  MenuItem,
  MenuList,
  Paper,
  Popper
} from '@mui/material';
import { Check, ChevronRight, Edit, RocketLaunch } from '@mui/icons-material';
import PopperSelect from '../../shared/components/PopperSelect';
import OverflowTooltip from '../../shared/OverflowTooltip';
import { FILTER_VALUES } from '../../shared/appConstants';
import { groupScrumSprintsByState } from '../../shared/scrumSprintUtils';

const STATE_PRESENTATION = {
  ACTIVE: { label: 'Active', icon: RocketLaunch },
  DRAFT: { label: 'Draft', icon: Edit },
  COMPLETED: { label: 'Completed', icon: Check }
};

const ScrumSprintStateSelect = ({
  sprints = [],
  value,
  onChange,
  allLabel = 'All Scrum Sprints'
}) => {
  const groups = useMemo(() => groupScrumSprintsByState(sprints), [sprints]);
  const [flyout, setFlyout] = useState(null);
  const [closeSignal, setCloseSignal] = useState(0);

  const renderValue = (selectedValue) => {
    if (!selectedValue || selectedValue === FILTER_VALUES.ALL) {
      return <Box component="span" className="filter-selected-value-text">{allLabel}</Box>;
    }

    const selectedGroup = groups.find((group) => String(group.value) === String(selectedValue));
    const selectedSprint = sprints.find((sprint) => String(sprint.id) === String(selectedValue));
    const selectedNames = selectedGroup?.sprints.map((sprint) => sprint?.name).filter(Boolean).join(', ')
      || selectedSprint?.name;
    return (
      <Box component="span" className="filter-selected-value-text">
        {selectedNames || allLabel}
      </Box>
    );
  };

  return (
    <>
    <PopperSelect
      value={value}
      onChange={onChange}
      displayEmpty
      renderValue={renderValue}
      closeSignal={closeSignal}
      MenuProps={{
        PaperProps: {
          className: 'scrum-sprint-state-menu',
          style: { width: 1, maxHeight: 260, overflow: 'auto' }
        }
      }}
    >
      {groups.map((group) => {
        const presentation = STATE_PRESENTATION[group.state] || {
          label: group.state.charAt(0) + group.state.slice(1).toLowerCase(),
          icon: Edit
        };
        const StateIcon = presentation.icon;

        const isExpanded = flyout?.group?.state === group.state;
        return (
          <MenuItem
            key={group.state}
            value={group.value}
            className={`scrum-sprint-state-option scrum-sprint-state-option--${group.state.toLowerCase()}`}
            aria-label={`${presentation.label}: ${group.sprints.map((sprint) => sprint?.name).filter(Boolean).join(', ')}`}
          >
            <Box className="scrum-sprint-state-option__icon">
              <StateIcon />
            </Box>

            <Box className="scrum-sprint-state-option__content">
              <Box
                className="scrum-sprint-state-option__names"
              >
                {group.sprints.slice(0, 2).map((sprint) => (
                  <OverflowTooltip
                    key={sprint.id}
                    title={sprint?.name || 'Unnamed Sprint'}
                    tooltipWidth={350}
                    className="scrum-sprint-state-option__name"
                    placement="top-start"
                  />
                ))}
              </Box>
              {group.state === 'ACTIVE' && (
                <Box component="span" className="scrum-sprint-state-option__latest">Latest</Box>
              )}
            </Box>

            <Box component="span" className="scrum-sprint-state-option__badge">
              <Box component="span" className="scrum-sprint-state-option__badge-dot" />
              {presentation.label}
            </Box>
            <IconButton
              className={`scrum-sprint-state-option__expand${isExpanded ? ' scrum-sprint-state-option__expand--open' : ''}`}
              aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${presentation.label} sprints`}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setFlyout(isExpanded ? null : { anchorEl: event.currentTarget, group });
              }}
              size="small"
            >
              <ChevronRight className="scrum-sprint-state-option__chevron" />
            </IconButton>
          </MenuItem>
        );
      })}
    </PopperSelect>

    <Popper
      open={Boolean(flyout)}
      anchorEl={flyout?.anchorEl}
      placement="right-start"
      className="scrum-sprint-individual-popper"
      modifiers={[
        { name: 'offset', options: { offset: [0, 7] } },
        {
          name: 'flip',
          enabled: true,
          options: { fallbackPlacements: ['left-start', 'right-end', 'left-end'] }
        },
        { name: 'preventOverflow', enabled: true, options: { boundary: 'viewport', padding: 8 } }
      ]}
    >
      <ClickAwayListener onClickAway={() => setFlyout(null)}>
        <Paper className="scrum-sprint-individual-menu" elevation={8}>
          <Box className="scrum-sprint-individual-menu__title">
            {flyout?.group?.state === 'ACTIVE' ? 'Active' : flyout?.group?.state === 'COMPLETED' ? 'Completed' : 'Draft'} Sprints
          </Box>
          <MenuList dense>
            {(flyout?.group?.sprints || []).map((sprint) => (
              <MenuItem
                key={`${flyout.group.state}-${sprint.id}`}
                selected={String(value) === String(sprint.id)}
                className={`scrum-sprint-individual-option scrum-sprint-individual-option--${flyout.group.state.toLowerCase()}`}
                aria-label={`Select ${sprint?.name || 'Unnamed Sprint'}`}
                onClick={() => {
                  onChange({ target: { value: String(sprint.id) } });
                  setFlyout(null);
                  setCloseSignal((signal) => signal + 1);
                }}
              >
                <Box component="span" className="scrum-sprint-individual-option__dot" />
                <OverflowTooltip
                  title={sprint?.name || 'Unnamed Sprint'}
                  tooltipWidth={350}
                  className="scrum-sprint-individual-option__name"
                  placement="top-start"
                />
              </MenuItem>
            ))}
          </MenuList>
        </Paper>
      </ClickAwayListener>
    </Popper>
    </>
  );
};

ScrumSprintStateSelect.propTypes = {
  sprints: PropTypes.arrayOf(PropTypes.object),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  allLabel: PropTypes.string,
};

export default ScrumSprintStateSelect;
